import React, { Fragment, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import omit from 'lodash.omit';

import CardDesignerCardElementResizeResizer from './CardDesignerCardElementResizeResizer';
import { RESIZER_POSITIONS } from './constants';

const propTypes = {
  element: PropTypes.shape({
    cardElement: PropTypes.shape({
      id: PropTypes.string.isRequired,
    }).isRequired,
    cardElementConfiguration: PropTypes.shape({
      id: PropTypes.string,
    }).isRequired,
  }).isRequired,
  localElement: PropTypes.shape({
    cardElement: PropTypes.shape({
      id: PropTypes.string.isRequired,
    }).isRequired,
    cardElementConfiguration: PropTypes.shape({
      id: PropTypes.string,
    }).isRequired,
  }).isRequired,
  setLocalElement: PropTypes.func.isRequired,
  cardBounds: PropTypes.shape({
    x: PropTypes.number,
    y: PropTypes.number,
  }),
  cardElementBounds: PropTypes.shape({
    width: PropTypes.number,
    height: PropTypes.number,
  }).isRequired,
  onCardElementUpdate: PropTypes.func.isRequired,
};

const CardDesignerCardElementResizeControls = ({
  element,
  localElement,
  setLocalElement,
  cardBounds,
  cardElementBounds,
  onCardElementUpdate,
}) => {
  const [resizer, setResizer] = useState(null);
  const [resizeData, setResizeData] = useState(null);
  const [resizedElement, setResizedElement] = useState(false);
  const [initialMousePosition, setInitialMousePosition] = useState({});
  const { frame } = localElement.cardElement;

  useEffect(() => {
    const onResize = event => {
      const { clientX, clientY } = event;
      const { x, y, width, height } = cardElementBounds;
      const { x: cardX, y: cardY } = cardBounds;
      const { x: initialClientX = clientX, y: initialClientY = clientY } =
        initialMousePosition;
      const {
        width: widthPercent,
        height: heightPercent,
        x: xPercent,
        y: yPercent,
      } = frame;

      let newX = x;
      let newY = y;
      let newWidth = width;
      let newHeight = height;

      const toppedCardX = Math.floor(cardX ?? 0);
      const toppedCardY = Math.floor(cardY ?? 0);

      // Determine if card is out of bounds on the top or left
      const xOutOfBounds = clientX < toppedCardX;
      const yOutOfBounds = clientY < toppedCardY;

      const minClientX = Math.max(clientX, toppedCardX);
      const minClientY = Math.max(clientY, toppedCardY);

      switch (resizer) {
        case RESIZER_POSITIONS.NW:
          newWidth = width + initialClientX - minClientX;
          newHeight = height + initialClientY - minClientY;
          newX = xOutOfBounds ? 0 : Math.floor(x + minClientX - initialClientX);
          newY = yOutOfBounds ? 0 : Math.floor(y + minClientY - initialClientY);
          break;
        case RESIZER_POSITIONS.NE:
          newWidth = width + minClientX - initialClientX;
          newHeight = height + initialClientY - minClientY;
          newY = yOutOfBounds ? 0 : Math.floor(y + minClientY - initialClientY);
          break;
        case RESIZER_POSITIONS.SE:
          newWidth = width + minClientX - initialClientX;
          newHeight = height + minClientY - initialClientY;
          break;
        case RESIZER_POSITIONS.SW:
          newWidth = width + initialClientX - minClientX;
          newHeight = height + minClientY - initialClientY;
          newX = xOutOfBounds ? 0 : Math.floor(x + clientX - initialClientX);
          break;
        default:
          break;
      }

      // We now have to calculate newWidthPercent and newHeightPercent
      // these are basically calculated using a Rule of 3

      //  use Rule of 3 to determine the appropriate % value
      let newWidthPercent = parseFloat(
        ((newWidth * widthPercent) / width).toFixed(2),
      );
      let newHeightPercent = parseFloat(
        ((newHeight * heightPercent) / height).toFixed(2),
      );
      let newXPercent =
        Math.round(parseFloat((newX * xPercent) / x) * 1e3) / 1e3;
      let newYPercent =
        Math.round(parseFloat((newY * yPercent) / y) * 1e3) / 1e3;

      if (Number.isNaN(newXPercent) || newXPercent < 0.001) {
        newXPercent = 0.001;
      }

      if (Number.isNaN(newYPercent) || newYPercent < 0.001) {
        newYPercent = 0.001;
      }

      // if (
      //   [RESIZER_POSITIONS.NW, RESIZER_POSITIONS.NE].includes(resizer) &&
      //   newXPercent === 0.01
      // ) {
      //   // When we reach the corner, avoid increasing the width over the max
      //   newWidthPercent = widthPercent;
      // }

      // Ensure that the item cannot grow past 0.99
      // this leaves a small, but useful margin that
      // prevents the image from being impossible to resize
      if (newWidthPercent + newXPercent >= 0.999) {
        newWidthPercent = 0.999 - newXPercent;
      }

      if (newHeightPercent + newYPercent >= 0.999) {
        newHeightPercent = 0.999 - newYPercent;
      }

      setResizedElement(true);
      setResizeData({
        width: newWidthPercent,
        height: newHeightPercent,
        x: newXPercent,
        y: newYPercent,
      });

      setLocalElement({
        ...localElement,
        cardElement: {
          ...localElement.cardElement,
          frame: {
            ...frame,
            width: newWidthPercent,
            height: newHeightPercent,
            x: newXPercent,
            y: newYPercent,
          },
        },
      });
    };

    const onStopResize = () => {
      window.removeEventListener('mousemove', onResize);
      setResizer(null);
    };

    if (resizer) {
      window.addEventListener('mousemove', onResize);
      window.addEventListener('mouseup', onStopResize);
    }

    return () => {
      window.removeEventListener('mousemove', onResize);
      window.removeEventListener('mouseup', onStopResize);
    };
  }, [resizer]);

  useEffect(() => {
    // We have finished resizing the element when the resizer resets.
    // If at said point, we have resized the element, and have resize data,
    // update the objects accordingly.
    if (!resizer && resizedElement && resizeData) {
      onCardElementUpdate({
        cardElement: element.cardElement,
        data: {
          frame: {
            ...omit(frame, '__typename'),
            ...omit(resizeData, '__typename'),
          },
        },
      });
      setResizedElement(false);
      setResizeData(null);
    }
  }, [resizer, resizedElement, resizeData]);

  const onResizerMouseDown = (event, position) => {
    const { clientX, clientY } = event;
    setInitialMousePosition({ x: clientX, y: clientY });
    setResizer(position);
  };

  return (
    <Fragment>
      <CardDesignerCardElementResizeResizer
        position={RESIZER_POSITIONS.SE}
        onMouseDown={event => onResizerMouseDown(event, RESIZER_POSITIONS.SE)}
      />
      <CardDesignerCardElementResizeResizer
        position={RESIZER_POSITIONS.SW}
        onMouseDown={event => onResizerMouseDown(event, RESIZER_POSITIONS.SW)}
      />
      <CardDesignerCardElementResizeResizer
        position={RESIZER_POSITIONS.NW}
        onMouseDown={event => onResizerMouseDown(event, RESIZER_POSITIONS.NW)}
      />
      <CardDesignerCardElementResizeResizer
        position={RESIZER_POSITIONS.NE}
        onMouseDown={event => onResizerMouseDown(event, RESIZER_POSITIONS.NE)}
      />
    </Fragment>
  );
};

CardDesignerCardElementResizeControls.propTypes = propTypes;

export default CardDesignerCardElementResizeControls;
